import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { AveragePlayerScore } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BrandS, LabelM } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { ClickMarketHomeSection_Target } from '@sorare/core/src/protos/events/so5/shared/events';
import { mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import { AnyPlayerStatsIcon } from '#src/components/player/AnyPlayerStatsIcon/index.tsx';

import type { TrendingPlayer_anyPlayer } from './__generated__/index.graphql.ts';

const Root = styled(Horizontal).attrs({ as: LinkBox })`
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-150);
  min-height: 66px;
  min-width: 290px;
  padding: 0 var(--intermediate-unit);
  align-items: stretch;
  @media ${mobileAndAbove} {
    min-width: 320px;
  }
`;

const ImgWrapper = styled.div`
  position: relative;
  isolation: isolate;
  width: 60px;
  flex-shrink: 0;
  flex-grow: 0;
  overflow: hidden;
`;
const StyledResponsiveImg = styled(ResponsiveImg)`
  position: absolute;
  left: 0;
  top: var(--unit);
  width: 60px;
`;

const Metas = styled(Vertical).attrs({ gap: 0.5 })`
  flex-grow: 1;
  justify-content: center;
`;

const StyledBrandS = styled(BrandS)`
  white-space: nowrap;
`;

const DEFAULT_MODE = AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE;

type Props = {
  anyPlayer: TrendingPlayer_anyPlayer;
  tradesCount: number;
  onMarketClick: (slug: string) => void;
  onStatsClick: (slug: string) => void;
  track?: (p: {
    target: ClickMarketHomeSection_Target;
    playerSlug?: string;
  }) => void;
};

export const TrendingPlayer = ({
  anyPlayer,
  tradesCount,
  onMarketClick,
  onStatsClick,
  track,
}: Props) => {
  const { displayName, fullPictureUrl } = anyPlayer;
  const mode = DEFAULT_MODE;
  return (
    <Root>
      <ImgWrapper>
        <StyledResponsiveImg src={fullPictureUrl} cropWidth={80} alt="" />
      </ImgWrapper>
      <Metas>
        <StyledBrandS>
          <LinkOverlay
            as="button"
            onClick={() => {
              onMarketClick(anyPlayer.slug);
              track?.({
                target: ClickMarketHomeSection_Target.PLAYER_MARKET_DRAWER,
                playerSlug: anyPlayer.slug,
              });
            }}
          >
            {displayName}
          </LinkOverlay>
        </StyledBrandS>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage
            id="TrendingPlayer.tradesCount"
            defaultMessage="{tradesCount, plural, one {1 transfer} other {# transfers}}"
            values={{ tradesCount }}
          />
        </LabelM>
      </Metas>
      <Horizontal>
        <PlayerScore player={anyPlayer} mode={mode} animated={false} />
        <AnyPlayerStatsIcon
          player={anyPlayer}
          onClick={() => {
            onStatsClick(anyPlayer.slug);
          }}
        />
      </Horizontal>
    </Root>
  );
};

TrendingPlayer.fragments = {
  anyPlayer: gql`
    fragment TrendingPlayer_anyPlayer on AnyPlayerInterface {
      slug
      sport
      displayName
      fullPictureUrl
      ...AnyPlayerStatsIcon_anyPlayer
      ...PlayerScore_anyPlayer
    }
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${PlayerScore.fragments.anyPlayer}
  ` as TypedDocumentNode<TrendingPlayer_anyPlayer>,
};
